import { FlashList, type ListRenderItem } from '@shopify/flash-list';
import { memo, useCallback, useMemo } from 'react';

import { Stack } from '@/components/layout';
import { SvgIcon, Text } from '@/components/ui';
import { STRINGS } from '@/constants/strings';
import type { FamilyAgendaGroup } from '@/features/guardian/components/family-agenda-section/types';
import { groupFamilyAgendaByDate } from '@/features/guardian/components/family-agenda-section/utils';
import type { FamilyEvent } from '@/features/guardian/family-events';
import { createStyles, useScaling } from '@/hooks';
import { colors, guardianColors } from '@/theme/colors';
import CalendarListRow from './calendar-list-row';

const strings = STRINGS.guardian.kids;

export type CalendarListViewProps = {
  /** Upcoming events, already filtered by the active player and sorted ascending (BR-05). */
  events: FamilyEvent[];
  onEventPress: (event: FamilyEvent) => void;
  contentBottomInset: number;
};

/**
 * All upcoming events grouped by date — the Calendar screen's List mode.
 *
 * Each day is one virtualized {@link FlashList} row: a date label plus a single card that
 * holds every event for that day, split by hairline dividers (like the Family Agenda
 * section) instead of a standalone card per event.
 */
function CalendarListView({ events, onEventPress, contentBottomInset }: CalendarListViewProps) {
  const { scale } = useScaling();
  const styles = useStyles();

  const groups = useMemo(() => groupFamilyAgendaByDate(events), [events]);

  const renderItem = useCallback<ListRenderItem<FamilyAgendaGroup>>(
    ({ item, index }) => (
      <Stack paddingTop={index === 0 ? 0 : scale(16)} gap={scale(8)}>
        <Text variant="Font Guardian App/Subtitle/Subtitle 12 Semibold" style={styles.dateLabel}>
          {item.label}
        </Text>
        <Stack overflow="hidden" style={styles.groupCard}>
          {item.items.map((event, eventIndex) => (
            <CalendarListRow
              key={event.id}
              event={event}
              showDivider={eventIndex !== item.items.length - 1}
              onPress={onEventPress}
            />
          ))}
        </Stack>
      </Stack>
    ),
    [onEventPress, scale, styles.dateLabel, styles.groupCard],
  );

  if (groups.length === 0) {
    return (
      <Stack flex={1} align="center" justify="center" paddingX={scale(20)} gap={scale(12)}>
        <Stack
          width={scale(56)}
          height={scale(56)}
          borderRadius={scale(28)}
          align="center"
          justify="center"
          backgroundColor={guardianColors['1']}>
          <SvgIcon name="IcCalendar" size={scale(28)} color={guardianColors['6']} />
        </Stack>
        <Text variant="Font Guardian App/Subtitle/Subtitle 14 Semibold" style={styles.emptyText}>
          {strings.noUpcomingEvents}
        </Text>
      </Stack>
    );
  }

  return (
    <FlashList
      data={groups}
      renderItem={renderItem}
      keyExtractor={group => group.key}
      showsVerticalScrollIndicator={false}
      contentContainerStyle={{
        paddingHorizontal: scale(20),
        paddingTop: scale(4),
        paddingBottom: contentBottomInset,
      }}
    />
  );
}

const useStyles = createStyles(({ scale }) => ({
  dateLabel: {
    color: guardianColors['6'],
  },
  groupCard: {
    borderRadius: scale(12),
    backgroundColor: colors['0'],
  },
  emptyText: {
    color: guardianColors['6'],
    textAlign: 'center',
  },
}));

export default memo(CalendarListView);
